import { MoreVertical, Clock, CheckCircle2, Save, Play, RefreshCw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CORSImage } from "@/components/ui/cors-image";
import YoutubeLogo from "../../assets/yt-logo.svg";

type UploadCardProps = {
  // Core required props
  title: string;
  thumbnail: string;
  timestamp: string;
  status: "draft" | "scheduled" | "published";
  
  // Optional core props
  youtube_video_id?: string;
  tags?: string[];
  views?: number;
  
  // YouTube engagement data (from cached stats)
  engagement?: {
    views: string | number;
    likes: string | number;
    comments: string | number;
    publishedAt: string;
    dataSource?: 'cached_fresh' | 'cached_stale' | 'youtube_api';
    lastUpdated?: string;
    syncAge?: number;
    isLive?: boolean;
  };
  viewCount?: string | number;
  likeCount?: string | number;
  commentCount?: string | number;
  dataFreshness?: 'live' | 'cached';
  
  // Action handlers
  onEdit?: () => void;
  onDelete?: () => void;
  onRefreshStats?: () => void;
  
  // UI state
  isDeleting?: boolean;
  isRefreshingStats?: boolean;
};

// Helper function to format large numbers
const formatNumber = (num: string | number): string => {
  const numValue = typeof num === 'string' ? parseInt(num) || 0 : num;
  
  if (numValue >= 1000000) {
    return (numValue / 1000000).toFixed(1) + 'M';
  }
  if (numValue >= 1000) {
    return (numValue / 1000).toFixed(1) + 'K';
  }
  return numValue.toString();
};

// Helper function to format dates
const formatTimestamp = (timestamp: string, status: string) => {
  let dateStr = timestamp
    .replace(/^(Published at |Scheduled for |Last edited |published on |scheduled for |edited on )/i, '')
    .replace(/ at \d{1,2}:\d{2}( am| pm)?$/i, '');
  
  try {
    let date: Date;
    
    if (dateStr.includes('/')) {
      const parts = dateStr.split(', ');
      if (parts.length === 2) {
        const [datePart, timePart] = parts;
        const [day, month, year] = datePart.split('/').map(Number);
        const [hour, minute, second] = timePart.split(':').map(Number);
        date = new Date(year, month - 1, day, hour, minute, second);
      } else {
        const [day, month, year] = dateStr.split('/').map(Number);
        date = new Date(year, month - 1, day);
      }
    } else {
      date = new Date(dateStr);
    }
    
    if (isNaN(date.getTime())) {
      return timestamp.toLowerCase();
    }
    
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (status === 'scheduled') {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[date.getDay()];
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      const time = date.toLocaleString('en-GB', timeOptions).toLowerCase();
      
      const futureDiffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (futureDiffDays === 0) {
        return `scheduled for today at ${time}`;
      } else if (futureDiffDays === 1) {
        return `scheduled for tomorrow at ${time}`;
      } else {
        return `scheduled for ${dayName.toLowerCase()} at ${time}`;
      }
    }
    
    if (status === 'published') {
      if (diffMinutes < 60) {
        return `Published ${diffMinutes} minutes ago`;
      } else if (diffHours < 24) {
        return `Published ${diffHours} hours ago`;
      } else if (diffDays === 1) {
        return `Published yesterday`;
      } else if (diffDays < 7) {
        return `Published ${diffDays} days ago`;
      } else {
        const weeks = Math.floor(diffDays / 7);
        return `Published ${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
      }
    } else {
      if (diffMinutes < 60) {
        return `Edited ${diffMinutes} minutes ago`;
      } else if (diffHours < 24) {
        return `Edited ${diffHours} hours ago`;
      } else if (diffDays === 1) {
        return `Edited yesterday`;
      } else if (diffDays < 7) {
        return `Edited ${diffDays} days ago`;
      } else {
        const weeks = Math.floor(diffDays / 7);
        return `Edited ${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
      }
    }
  } catch (error) {
    return timestamp.toLowerCase();
  }
};

export function UploadCard({
  title,
  thumbnail,
  timestamp,
  status,
  youtube_video_id,
  onEdit,
  onDelete,
  onRefreshStats,
  isDeleting,
  isRefreshingStats,
  views,
  engagement,
  viewCount,
  likeCount,
  commentCount,
  dataFreshness
}: UploadCardProps) {
  // Define statusIcon inside the component function
const statusIcon = {
  draft: <Save className="text-yellow-400 w-4 h-4" />,
  scheduled: <Clock className="text-blue-400 w-4 h-4" />,
  published: (
    <div className="relative flex items-center justify-center">
     
      <Play className="relative w-3 h-3 text-white fill-white z-10" fill="white" />
    </div>
  ),
};
  // Get display views with proper priority (now using cached data)
  const getDisplayViews = () => {
    if (status !== 'published') return null;
    
    // Priority: engagement.views > viewCount > views
    if (engagement?.views !== undefined) {
      return engagement.views;
    }
    if (viewCount !== undefined) {
      return viewCount;
    }
    if (views !== undefined) {
      return views;
    }
    return 0; // Default to 0 instead of dummy data
  };

  const displayViews = getDisplayViews();
  
  // Show refresh indicator if data is stale
  const isDataStale = engagement?.dataSource === 'cached_stale';
  const showStaleIndicator = isDataStale && engagement?.syncAge && engagement.syncAge > 120; // older than 2 hours

  return (
    <div className="flex flex-col rounded-md overflow-hidden font-medium">
      {/* Thumbnail */}
      <div className="relative w-full overflow-hidden rounded-md" style={{ aspectRatio: "16/9" }}>
        <CORSImage
          src={thumbnail}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <img
          src={YoutubeLogo}
          alt="YouTube"
          className="absolute bottom-2 right-2 h-5 w-auto opacity-90"
        />
        
        {/* Dropdown Menu */}
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger className="p-1 text-white/80 hover:text-white focus:outline-none bg-black/30 rounded hover:bg-black/50 transition-colors">
              <MoreVertical className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[var(--sidebar)] text-white border border-muted-foreground"
            >
              {onEdit && (
                <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem 
                  onClick={onDelete} 
                  disabled={isDeleting}
                  className={isDeleting ? "opacity-50" : ""}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </DropdownMenuItem>
              )}
              {youtube_video_id && onRefreshStats && (
                <DropdownMenuItem 
                  onClick={onRefreshStats}
                  disabled={isRefreshingStats}
                  className={isRefreshingStats ? "opacity-50" : ""}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshingStats ? 'animate-spin' : ''}`} />
                  {isRefreshingStats ? "Refreshing..." : "Refresh Stats"}
                </DropdownMenuItem>
              )}
              {youtube_video_id && (
                <DropdownMenuItem asChild>
                  <a
                    href={`https://youtube.com/watch?v=${youtube_video_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on YouTube
                  </a>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Content */}
      <div className="pt-4 space-y-1">
        {/* Title */}
        <h3 className="text-white text-m font-medium leading-tight line-clamp-1 pb-1">
          {title}
        </h3>

        {/* Views and Status - Combined on same line */}
        <div className="flex items-center gap-1.5 text-sm text-white/60">
          {/* Views for published videos - NOW ENABLED with cached data */}
         {} {status === 'published' && displayViews !== null && (
            <>
              <div className="flex items-center gap-1.5">
                {statusIcon[status]}
                <span>{formatNumber(displayViews)} views</span>
              </div>
              <span>-</span>
            </>
          )} 
          
          {/* Status */}
          <div className="flex items-center gap-1">
           
            <span>{formatTimestamp(timestamp, status)}</span>
          </div>
        </div>

        {/* Data freshness indicator (optional - for debugging) 
        {engagement?.dataSource && process.env.NODE_ENV === 'development' && (
          <div className="text-xs text-white/40">
            {engagement.dataSource === 'cached_fresh' && 'Fresh cached data'}
            {engagement.dataSource === 'cached_stale' && 'Updating stats...'}
            {engagement.dataSource === 'youtube_api' && 'Live data'}
            {engagement.syncAge && ` (${Math.round(engagement.syncAge)}m ago)`}
          </div>
          */}
      </div>
    </div>
  );
}